import { EventInput } from '@fullcalendar/core';

export const mapRoomSchedulesToEvents = (roomSchedules: any[]): EventInput[] => {
  return roomSchedules.flatMap(roomSchedule => {
    const events: EventInput[] = [];

    const roomId = roomSchedule.id;
    const roomName = roomSchedule.title;
    const schedule = roomSchedule.extendedProps?.schedule;

    if (!schedule) return events;

    const date = schedule.date;

    if (schedule.status === 'AVAILABLE') {
      const shiftStart = schedule.shiftStart;
      const shiftEnd = schedule.shiftEnd;

      // Before shift (unavailable)
      if (shiftStart && shiftStart > '00:00:00') {
        events.push({
          id: `unavailable-before-${schedule.id}`,
          resourceId: roomId,
          start: `${date}T00:00:00`,
          end: `${date}T${shiftStart}`,
          display: 'background',
          title: 'მიუწვდომელი',
          backgroundColor: '#000000',
          borderColor: '#f0f0f0',
          textColor: '#ffffff',
          className: 'day-off',
          extendedProps: {
            type: 'outside-shift',
            position: 'before',
            roomName: roomName,
            bookingDate: date
          }
        });
      }

      // After shift (unavailable)
      if (shiftEnd && shiftEnd < '23:59:59') {
        events.push({
          id: `unavailable-after-${schedule.id}`,
          resourceId: roomId,
          start: `${date}T${shiftEnd}`,
          end: `${date}T23:59:59`,
          display: 'background',
          title: 'მიუწვდომელი',
          backgroundColor: '#000000',
          borderColor: '#f0f0f0',
          textColor: '#ffffff',
          className: 'day-off',
          extendedProps: {
            type: 'outside-shift',
            position: 'after',
            roomName: roomName,
            bookingDate: date
          }
        });
      }

      // Breaks during shift
      for (const b of schedule.breaks || []) {
        events.push({
          id: `break-${schedule.id}-${b.start}-${b.end}`,
          resourceId: roomId,
          start: `${date}T${b.start}`,
          end: `${date}T${b.end}`,
          title: `⚠ ${b.type}`,
          display: 'background',
          backgroundColor: 'rgba(250, 173, 20, 0.2)',
          borderColor: '#faad14',
          extendedProps: {
            type: 'break',
            breakType: b.type,
            roomName: roomName,
            bookingDate: date
          }
        });
      }
    } else if (schedule.status === 'UNAVAILABLE' || schedule.status === 'MAINTENANCE') {
      // For UNAVAILABLE / MAINTENANCE - entire day blocked
      events.push({
        id: `blocked-${schedule.id}`,
        resourceId: roomId,
        start: `${date}T00:00:00`,
        end: `${date}T23:59:59`,
        title: schedule.status === 'UNAVAILABLE' ? 'მიუწვდომელი' : 'მოვლა-შენახვა',
        display: 'background',
        backgroundColor: schedule.status === 'MAINTENANCE' ? 'rgba(255, 77, 79, 0.1)' : '#fafafa',
        borderColor: schedule.status === 'MAINTENANCE' ? '#ff4d4f' : '#d9d9d9',
        extendedProps: {
          type: 'room-unavailable',
          roomName: roomName,
          status: schedule.status,
          bookingDate: date
        }
      });
    }

    return events;
  });
};

/**
 * beaut-546: `fallbackDate` is required to draw anything for an employee whose
 * `schedule` is null — the roster row is where the date normally comes from.
 *
 * The backend only sends null schedules when a caller asks for the
 * unscheduled employees (the reassign screen). Callers that do not pass a date
 * get no events for those rows rather than a crash, which is what the old code
 * did here: it read `schedule.date` unguarded and threw on the first null.
 */
export const mapEmployeeSchedulesToEvents = (
  employeeSchedules: any[],
  fallbackDate?: string,
): EventInput[] => {


  return employeeSchedules.flatMap(employeeSchedule => {
    const events: EventInput[] = [];

    const empId = employeeSchedule.id;
    const fullName = employeeSchedule.fullName;
    const schedule = employeeSchedule.schedule;

    // No roster entered for this date. Not the same thing as a day off — the
    // operator simply has not filled the schedule in — but it shows the same
    // way, as a full non-working day, so the column reads honestly and a
    // booking can still be dragged onto it.
    if (!schedule) {
      if (!fallbackDate) {
        return events;
      }

      events.push({
        id: `no-schedule-${empId}-${fallbackDate}`,
        resourceId: empId,
        start: `${fallbackDate}T00:00:00`,
        end: `${fallbackDate}T23:59:59`,
        title: 'გრაფიკი არ აქვს',
        display: 'background',
        backgroundColor: '#dc939a',
        borderColor: '#ff0020',
        className: 'day-off',
        extendedProps: {
          type: 'no-schedule',
          employeeName: fullName,
          bookingDate: fallbackDate,
        },
      });

      return events;
    }

    const date = schedule.date;

    if (schedule.status === 'SCHEDULED') {
      const shiftStart = schedule.shiftStart;
      const shiftEnd = schedule.shiftEnd;

      // Before shift (non-working)
      if (shiftStart && shiftStart > '00:00:00') {
        events.push({
          id: `off-before-${schedule.id}`,
          resourceId: empId,
          start: `${date}T00:00:00`,
          end: `${date}T${shiftStart}`,
          display: 'background',
          title: 'არა სამუშაო',
          backgroundColor: '#000000', // gray
          borderColor: '#f0f0f0',
          textColor: '#ffffff',
          className: 'day-off',
          extendedProps: {
            type: 'off-hours',
            position: 'before',
            employeeName: fullName,
            bookingDate: date
          }
        });
      }

      // Add the actual shift (working hours)
      // events.push({
      //   id: `shift-${schedule.id}`,
      //   resourceId: empId,
      //   start: `${date}T${shiftStart}`,
      //   end: `${date}T${shiftEnd}`,
      //   title: `${fullName} (${shiftStart} - ${shiftEnd})`,
      //   backgroundColor: '#4CAF50', // green for working hours
      //   borderColor: '#388E3C',
      //   extendedProps: {
      //     type: 'shift',
      //     employeeName: fullName,
      //     bookingDate: date
      //   }
      // });

      // After shift (non-working)
      if (shiftEnd && shiftEnd < '23:59:59') {
        events.push({
          id: `off-after-${schedule.id}`,
          resourceId: empId,
          start: `${date}T${shiftEnd}`,
          end: `${date}T23:59:59`,
          display: 'background',
          title: 'არა სამუშაო',
          backgroundColor: '#000000',
          borderColor: '#f0f0f0',
          textColor: '#ffffff',
          className: 'day-off',
          extendedProps: {
            type: 'off-hours',
            position: 'after',
            employeeName: fullName,
            bookingDate: date
          }
        });
      }

      // Breaks during shift
      for (const b of schedule.breaks || []) {
        events.push({
          id: `break-${schedule.id}-${b.id}`,
          resourceId: empId,
          start: `${date}T${b.start}`,
          end: `${date}T${b.end}`,
          title: `⛔ ${b.type}`,
          display: 'background',
          backgroundColor: '#dc939a',
          borderColor: '#ff0020',
          extendedProps: {
            type: 'break',
            breakType: b.type,
            employeeName: fullName,
            bookingDate: date
          }
        });
      }
    } else {
      // For DAY_OFF / SICK_LEAVE / VACATION
      events.push({
        id: `off-${schedule.id}`,
        resourceId: empId,
        start: `${date}T00:00:00`,
        end: `${date}T23:59:59`,
        title: schedule.status,
        display: 'background',
        backgroundColor: '#dc939a',
        borderColor: '#ff0020',
        extendedProps: {
          type: 'day-off',
          employeeName: fullName,
          status: schedule.status,
          bookingDate: date
        }
      });
    }

    return events;
  });
}
